<script setup>
import { useUsersStore } from '@/stores'
import { mediaUrl } from '@core/utils/mediaUrl'
import { useDesignSystem } from '@/composables/useDesignSystem'
import Tooltip from '@/layouts/components/Tooltip.vue'
import {
  emailValidator,
  requiredValidator
} from '@validators'

const emit = defineEmits([
  'submit',
])

const { dialogWidth, smAndDown } = useDesignSystem()
const dWidth = dialogWidth('md')

const usersStore = useUsersStore()
let isEditingSecondaryMember  = null
let myUser = null;
({ myUser, isEditingSecondaryMember } = storeToRefs(usersStore))

const isSecondaryAccount = computed(() => !!myUser.value?.isSecondaryAccount)
const canSetSecondaryAccountEmail = ref(myUser?.value?.secondaryAccountEmail ? false: true)
const secondaryConfirmed = ref(false)
const inviteSentToEmail = ref('')

// RM-159: the Tier 2 opt-in is only meaningful when an active Platinum-spouse
// pair exists. On the secondary side the link is myUser.primaryAccount; on the
// primary side it's myUser.secondaryAccount. Both sides require Platinum (the
// secondary inherits it from the primary).
const hasActiveSpouse = computed(() => {
  const u = myUser.value
  if (!u) return false
  const linked = u.isSecondaryAccount ? !!u.primaryAccount : !!u.secondaryAccount
  
  return linked && !!u.isPlatinum
})

// RM-159: the spouse-access toggle is staged locally and only persisted when the
// member explicitly clicks "Save Changes" — giving a deliberate save moment and
// a clear confirmation, instead of the previous silent auto-save on flip.
const savingSpouseAccess = ref(false)
const spouseAccessSaved = ref(false)
const pendingSpouseAccess = ref(!!myUser?.value?.allowSpouseAccountAccess)

// Re-seed the staged value from the persisted setting whenever the dialog
// (re)opens or the saved value changes externally, so it always starts clean.
watch(
  () => [isEditingSecondaryMember.value, myUser.value?.allowSpouseAccountAccess],
  ([open]) => {
    if (open) pendingSpouseAccess.value = !!myUser.value?.allowSpouseAccountAccess
  },
  { immediate: true },
)

// Drop the "Saved" confirmation the moment the member edits the toggle again.
watch(pendingSpouseAccess, () => { spouseAccessSaved.value = false })

const hasUnsavedSpouseAccess = computed(
  () => pendingSpouseAccess.value !== !!myUser.value?.allowSpouseAccountAccess,
)

const saveSpouseAccess = async () => {
  savingSpouseAccess.value = true
  myUser.value.allowSpouseAccountAccess = pendingSpouseAccess.value
  try {
    const saveErrors = await usersStore.updateMyUser()
    if (!saveErrors) spouseAccessSaved.value = true
  } finally {
    savingSpouseAccess.value = false
  }
}

const onFormSubmit = async () => {
  form.value.validate().then(async ({valid: isValid}) => {
    if(isValid){
      saveSecondaryMember()
    }
  })
}
const removeSecondaryMember = async () => {
  myUser.value.secondaryAccountEmail = null
  await usersStore.updateMyUser()
  canSetSecondaryAccountEmail.value = true
  inviteSentToEmail.value = ''
}

const saveSecondaryMember = async () => {
  if(secondaryConfirmed.value){
    const invitedEmail = myUser.value.secondaryAccountEmail
    const saveErrors = await usersStore.updateMyUser()

    if (!saveErrors) {
      canSetSecondaryAccountEmail.value = false
      inviteSentToEmail.value = invitedEmail
    }
  }
}

const showRemoveConfirm = ref(false)

const form = ref()
const valid = ref(true)

const requestClose = () => {
  inviteSentToEmail.value = ''
  usersStore.hideSecondaryMemberDialog()
}
</script>

<template>
  <VDialog
    :width="smAndDown ? undefined : dWidth"
    :model-value="isEditingSecondaryMember"
    persistent
    :fullscreen="smAndDown"
  >
    <VToolbar
      dark
      color="primary"
      class="toolbar-header"
    >
      <VToolbarItems style="width: 58px" />
      <VSpacer />
      <VToolbarTitle>Spouse/Partner Account</VToolbarTitle>
      <VSpacer />
      <VToolbarItems>
        <VBtn
          icon="$close"
          size="large"
          variant="text"
          @click="requestClose"
        >
          <VIcon>mdi-close</VIcon>
        </VBtn>
      </VToolbarItems>
    </VToolbar>
    <VCard
      class="pa-sm-9 pa-2"
      :class="[smAndDown ? 'main-content' : '']"
    >
      <!-- Secondary account user viewing their primary account info -->
      <template v-if="isSecondaryAccount">
        <VCardText class="text-center">
          <div class="mt-2">
            <RouterLink
              v-if="myUser.primaryAccount?.profileId"
              :to="`/m/${myUser.primaryAccount.profileId}`"
              class="secondary-member-link d-flex flex-column align-center justify-center"
              @click="requestClose"
            >
              <VAvatar
                size="64"
                class="mb-2"
                :color="myUser.primaryAccount.profileImageId ? undefined : 'primary'"
              >
                <VImg
                  v-if="myUser.primaryAccount.profileImageId"
                  :src="mediaUrl(myUser.primaryAccount.profileImageId, 'small')"
                />
                <span
                  v-else
                  class="text-white text-h6"
                >{{ myUser.primaryAccount.fullName?.charAt(0) }}</span>
              </VAvatar>
              <span class="text-primary text-body-1 font-weight-medium">
                {{ myUser.primaryAccount.fullName }}
              </span>
            </RouterLink>
            <template v-else>
              <VAvatar
                size="64"
                class="mb-2"
                :color="myUser.primaryAccount?.profileImageId ? undefined : 'primary'"
              >
                <VImg
                  v-if="myUser.primaryAccount?.profileImageId"
                  :src="mediaUrl(myUser.primaryAccount.profileImageId, 'small')"
                />
                <span
                  v-else
                  class="text-white text-h6"
                >{{ myUser.primaryAccount?.fullName?.charAt(0) }}</span>
              </VAvatar>
              <div class="text-body-1 font-weight-medium">
                {{ myUser.primaryAccount?.fullName }}
              </div>
            </template>
            <div
              class="text-caption text-medium-emphasis"
              style="line-height: 1.2;"
            >
              (Spouse/Partner)
            </div>
            <div class="text-body-2 mt-1 text-medium-emphasis">
              {{ myUser.primaryAccount?.email }}
            </div>
            <div
              class="text-caption text-medium-emphasis mt-1"
            >
              (Primary member)
            </div>
          </div>
        </VCardText>

        <VCardText
          v-if="hasActiveSpouse"
          class="spouse-access-section"
        >
          <VSwitch
            v-model="pendingSpouseAccess"
            :disabled="savingSpouseAccess"
            color="primary"
            label="Let my spouse access and manage my account"
            hide-details
          />
          <div class="text-body-2 text-medium-emphasis mt-2">
            <template v-if="pendingSpouseAccess">
              Your spouse can view and manage your profile and reservations. They still can't change payments, membership, or delete your account.
            </template>
            <template v-else>
              Your spouse can submit reservations for you. They can't see your booking history or profile unless you turn on full access.
            </template>
          </div>
          <div class="d-flex align-center gap-3 mt-4">
            <VBtn
              color="primary"
              :loading="savingSpouseAccess"
              :disabled="!hasUnsavedSpouseAccess"
              @click="saveSpouseAccess"
            >
              Save Changes
            </VBtn>
            <span
              v-if="spouseAccessSaved && !hasUnsavedSpouseAccess"
              class="text-success d-inline-flex align-center text-body-2"
            >
              <VIcon
                size="18"
                class="mr-1"
              >mdi-check-circle</VIcon>
              Saved
            </span>
          </div>
        </VCardText>
      </template>

      <!-- Primary account user managing their secondary account -->
      <template v-else>
        <div class="text-center">
          <div
            v-if="myUser.secondaryAccount"
            class="mt-2"
          >
            <RouterLink
              :to="`/m/${myUser.secondaryAccount.profileId}`"
              class="secondary-member-link d-flex align-center justify-center"
              @click="requestClose"
            >
              <VAvatar
                size="32"
                class="mr-2"
                :color="myUser.secondaryAccount.profileImageId ? undefined : 'primary'"
              >
                <VImg
                  v-if="myUser.secondaryAccount.profileImageId"
                  :src="mediaUrl(myUser.secondaryAccount.profileImageId, 'small')"
                />
                <span
                  v-else
                  class="text-white text-caption"
                >{{ myUser.secondaryAccount.firstName?.charAt(0) }}{{ myUser.secondaryAccount.lastName?.charAt(0) }}</span>
              </VAvatar>
              <span class="d-flex flex-column">
                <span
                  class="text-primary text-body-2"
                  style="text-align: left;"
                >{{ myUser.secondaryAccount.firstName }} {{ myUser.secondaryAccount.lastName }}</span>
                <span
                  class="text-caption text-medium-emphasis"
                  style="line-height: 1.2;"
                >(Spouse/Partner)</span>
              </span>
            </RouterLink>
            <div class="text-body-2 mt-1">
              {{ myUser.secondaryAccount.email }}
            </div>
          </div>
        </div>
        <VCardText>
          <!-- 👉 Form -->
          <VForm
            ref="form"
            v-model="valid"
            validate-on="blur"
            @submit.prevent="onFormSubmit"
          >
            <VRow>
              <VCol
                cols="12"
              >
                <VTextField
                  v-if="canSetSecondaryAccountEmail"
                  v-model="myUser.secondaryAccountEmail"
                  autocomplete="null"
                  label="email address"
                  :disabled="!canSetSecondaryAccountEmail"
                  :rules="[requiredValidator, emailValidator]"
                />
                <VCheckbox
                  v-if="myUser.secondaryAccountEmail && canSetSecondaryAccountEmail"
                  v-model="secondaryConfirmed"
                  label="Confirm"
                  persistent-hint
                  hint="By checking above I verify that my secondary member is a spouse/partner/family member. I understand that my secondary member must abide by the same terms and conditions, failure to do so may result in the suspension/termination of both accounts."
                />
                <VAlert
                  v-if="inviteSentToEmail"
                  type="success"
                  variant="tonal"
                  class="mt-4"
                >
                  Invite sent to {{ inviteSentToEmail }}
                </VAlert>
              </VCol>


              <VCol
                cols="12"
                class="d-flex flex-wrap justify-center gap-4"
              >
                <template v-if="canSetSecondaryAccountEmail">
                  <VBtn
                    :disabled="!secondaryConfirmed || !valid"
                    type="submit"
                  >
                    Invite now
                  </VBtn>
                  <div class="text-caption w-100 text-center">
                    Spouse/Partner has full access to Platinum benefits
                  </div>
                  <div
                    v-if="secondaryConfirmed"
                    class="text-caption"
                  >
                    Your secondary account member will receive an email with a link to join.
                  </div>
                </template>
                <template v-else>
                  <div class="w-100 text-center mb-2">
                    <div
                      v-if="!myUser.secondaryAccount"
                      class="text-body-1 font-weight-medium"
                    >
                      {{ myUser.secondaryAccountEmail }}
                    </div>
                    <div class="text-caption">
                      {{ myUser.secondaryAccount ? '' : '(Invited)' }}
                    </div>
                  </div>
                  <VBtn @click="showRemoveConfirm = true">
                    Remove Spouse/Partner
                  </VBtn>
                  <div class="text-caption w-100 text-center">
                    Spouse/Partner has full access to Platinum benefits
                  </div>
                </template>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>

        <VCardText
          v-if="hasActiveSpouse"
          class="spouse-access-section"
        >
          <VSwitch
            v-model="pendingSpouseAccess"
            :disabled="savingSpouseAccess"
            color="primary"
            label="Let my spouse access and manage my account"
            hide-details
          />
          <div class="text-body-2 text-medium-emphasis mt-2">
            <template v-if="pendingSpouseAccess">
              Your spouse can view and manage your profile and reservations. They still can't change payments, membership, or delete your account.
            </template>
            <template v-else>
              Your spouse can submit reservations for you. They can't see your booking history or profile unless you turn on full access.
            </template>
          </div>
          <div class="d-flex align-center gap-3 mt-4">
            <VBtn
              color="primary"
              :loading="savingSpouseAccess"
              :disabled="!hasUnsavedSpouseAccess"
              @click="saveSpouseAccess"
            >
              Save Changes
            </VBtn>
            <span
              v-if="spouseAccessSaved && !hasUnsavedSpouseAccess"
              class="text-success d-inline-flex align-center text-body-2"
            >
              <VIcon
                size="18"
                class="mr-1"
              >mdi-check-circle</VIcon>
              Saved
            </span>
          </div>
        </VCardText>
      </template>
    </VCard>

    <VDialog
      v-model="showRemoveConfirm"
      max-width="400"
    >
      <VCard>
        <VCardTitle>Remove Spouse/Partner</VCardTitle>
        <VCardText>
          Are you sure you want to remove? They will no longer receive the benefits of your membership.
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="showRemoveConfirm = false"
          >
            Cancel
          </VBtn>
          <VBtn
            color="error"
            @click="removeSecondaryMember(); showRemoveConfirm = false"
          >
            Remove
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </VDialog>
</template>

<style lang="scss" scoped>
.v-toolbar-title{
  flex: unset;
  margin-inline-start:unset;
}

.toolbar-header{
  position: sticky;
  top: 0;
  z-index: 1;
}

.main-content{
  min-height: calc(100dvh - 64px);
  padding-bottom: 80px !important;
}

.secondary-member-link {
  padding: 6px 12px;
  border-radius: 4px;
  text-decoration: none;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
}
</style>
